/**
 * LoopBackBanner — surfaces when `state.loopBackPending` is true.
 *
 * Set by the reducer (see HYDRATE_FROM_BACKEND) when the backend
 * transitions from `risk_decisions` / `completed` back to
 * `controls_pending`. That only happens when the user picked
 * "Reduce" on at least one risk — the workflow has cycled back to
 * fold the new control / requirement into the residual analysis.
 *
 * Renders above whatever phase body is active. Dismissing the
 * banner clears the flag in the reducer; it will re-arm if another
 * Reduce loop-back fires later in the same session.
 */
import React from 'react';
import { Button } from '../../../../components/ui/Button2';
import { Icon } from '../../../../components/ui/Icon';
import styles from './LoopBackBanner.module.css';

// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

interface LoopBackBannerProps {
  onDismiss: () => void;
}

export default function LoopBackBanner({ onDismiss }: LoopBackBannerProps) {
  return (
    <div className={styles.banner} role="status">
      <span className={styles.icon} aria-hidden="true">
        <Icon name="refresh" size={14} />
      </span>
      <div className={styles.body}>
        <h3 className={styles.title}>Workflow looped back to Controls</h3>
        <p className={styles.desc}>
          One or more Reduce decisions added a new control or
          requirement. The residual analysis has cycled back so the
          attack-path and risk calculations can fold in the addition.
          Implement the new evidence, then continue the workflow as
          usual.
        </p>
      </div>
      <div className={styles.actions}>
        <ButtonAny variant="outline" size="sm" onClick={onDismiss}>
          Dismiss
        </ButtonAny>
      </div>
    </div>
  );
}
